import { red } from '@mui/material/colors';
import React, { useContext, useState } from 'react';
import { OrderType } from '../../../interfaces/delivery-company/orders';
import { get_cod } from '../../../utils/cod';
import { displayLocation } from '../../../utils/locations';
import { displayMoney } from '../../../utils/money';

import CopyLinkButton from '../../common/copy-link-button/copy-link-button';
import { OrderIdWithPopover } from '../../common/order-id-with-popover/order-id-with-popover';

import { GenericList } from '../../common/generic-list/generic-list';
import { IconWithText, mobileDoubleItemsGrid } from '../../common/styles/components';
import { showDeliveryBatchChip } from '../../common/styles/delivery-batch.styles';
import { getDeliveryStateColor, getDeliveryStateIcon, showDeliveryState } from '../../common/styles/order-states.style';
import { showPickupTaskChip } from '../../common/styles/pickup-task.styles';
import { showWorkflow } from '../../common/styles/workflow-display';
import { Updater } from 'use-immer';
import { SortingByColumnState } from '../../common/generic-table/utils';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import StorefrontIcon from '@mui/icons-material/Storefront';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import InventoryIcon from '@mui/icons-material/Inventory';
import ChecklistIcon from '@mui/icons-material/Checklist';
import LinkIcon from '@mui/icons-material/Link';
import OrderNotes from '../../common/order-notes/order-notes';
import CustomerWithNavigate from '../../common/customer-with-navigate/customer-with-navigate';
import IncompleteAddressError from '../../common/incomplete-address-error/incomplete-address-error';
import { Download, Info } from '@mui/icons-material';
import { GiCrackedGlass } from 'react-icons/gi';
import { OrgSettingsContext } from '../../../contexts/org-settings-context';
import { DialogTitle, Typography, DialogContent, IconButton } from '@mui/material';
import { PrintWaybillsMultiSizes } from '../../common/bulk-action/print-waybills';
import CustomDialog from '../../common/custom-dialog/custom-dialog';
import { displayOrderSource } from '../../common/display-order-source/display-order-source';
import { QuickFilterScope } from '../../../enums/quick-filters';
import { IQuickFilter } from '../../../interfaces/common/quick-filters';
import { AuthContext } from '../../../contexts/user-context/user-context';


interface ResponsiveOrdersListingProps {
    ordersList: OrderType[]
    selectedOrders: Map<string, OrderType>
    updateSelectedOrders: Updater<Map<string, OrderType>>
    isLoading: boolean
    sorting: SortingByColumnState
    setSorting: React.Dispatch<React.SetStateAction<SortingByColumnState>>
    applyQuickFilter: (quickFilter: IQuickFilter | null) => void;
    sourceView: string;
}

const ResponsiveOrdersListing = ({ ordersList, selectedOrders, updateSelectedOrders, isLoading, sorting, setSorting, applyQuickFilter, sourceView }: ResponsiveOrdersListingProps) => {
    const { color } = useContext(OrgSettingsContext)
    const {user} = useContext(AuthContext)
    return <>
        <GenericList<OrderType>
            items={ordersList}
            applyQuickFilter={applyQuickFilter}
            sourceView={sourceView}
            selectionEnabled
            quickFiltersScope={QuickFilterScope.ORDER}
            selectedItems={selectedOrders}
            updateSelectedItems={updateSelectedOrders}
            isLoading={isLoading}
            fullWidth
            columns={[
                {
                    header: <><b>Order Id <br /> (Ref #)</b></>,
                    id: "order_id",
                    display_body: (order) => (
                        <OrderIdWithPopover order={order} />
                    )
                },
                {
                    header: <div
                        onClick={() => setSorting({
                            ...sorting,
                            order: sorting.order === 'increasing'
                                ? 'decreasing'
                                : 'increasing',
                        })}
                        style={{
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <b>
                            Creation Date
                            {sorting.order === 'increasing' ? (
                                <KeyboardArrowUpIcon fontSize="small" />
                            ) : (
                                <KeyboardArrowDownIcon fontSize="small" />
                            )}
                        </b>
                    </div>,
                    display_body: (order) => <>{order.creation_date !== undefined &&
                        new Date(order.creation_date).toLocaleString()}
                        
                        {displayOrderSource(order.source)}
                        </>,
                    id: "creation_date"
                },
                {
                    header: <b>Customer</b>,
                    id: "customer",
                    display_body: (order) => <>
                        <CustomerWithNavigate customer={order.customer!} location={order.customer_location} />
                        <br />
                    </>,
                },
                {
                    header: <b>Attention</b>,
                    id: 'attention',
                    display_body: (order) => <>
                    {order.is_critical && <IconWithText centered><Info color='warning' /> <b>Critical</b></IconWithText>}
        
                        {order.is_fragile && <IconWithText centered><GiCrackedGlass size={16} color={color} /> <b color={color}>Fragile</b></IconWithText>}          
                    </>
                },
                {
                    header: <b>Workflow</b>,
                    id: "workflow",
                    display_body: (order) => showWorkflow(order.workflow, order.is_exchange, order.exchange_for, order.return_reason)
                },
                {
                    header: <b>Delivery Attempts</b>,
                    id: "attempts count",
                    display_body: (order) => order.delivery_attempts_count
                },
                {
                    header: <b>POP</b>,
                    id: "pop",
                    display_body: (order) => displayMoney(order.pop),
                },
                {
                    header: <b>COD</b>,
                    id: "cod",
                    display_body: (order) => displayMoney(get_cod(order)),
                },
                {
                    header: <b> Product Price</b>,
                    id: "Product price",
                    display_body: (order) => order.product_price ? <>{order.product_price} $</> : "",
                    hidden: !(user?.product_price_enabled) 
                },
                {
                header: <b>Notes</b>,
                id: 'notes',
                display_body: (order) => <>
                    {!order.customer_location?.area && <IncompleteAddressError /> }
                    <OrderNotes order={order} />
                </>,
                },
                {
                    header: <b>Status</b>,
                    id: "status",
                    display_body: (order) => showDeliveryState(order.delivery_state),
                }
            ]}
            getItemUID={order => order.id}
            rowConfigs={[
                {
                    display_row: (order) => <b>Order #{order.order_id}</b>
                },
                {
                    display_row: (order) => <>{order.creation_date && new Date(order.creation_date).toLocaleDateString()}</>
                },
                {
                    display_row: (order) => <IconWithText><PersonOutlineIcon color='primary' /> <div onClick={(e) => { e.stopPropagation(); window.open(`/customer/${order.customer?.id}`) }}><b>{order.customer?.name} {order.customer?.phone_number}</b></div></IconWithText>
                },
                {
                    display_row: (order) => mobileDoubleItemsGrid([<div key="cod"><b>COD: </b> {displayMoney(get_cod(order))}</div>, <div key="pop"><b>POP: </b> {displayMoney(order.pop)}</div>])
                },
                {
                    display_row: (order) => <>
                        {!order.customer_location?.area && <IncompleteAddressError /> }
                        <OrderNotes order={order} />
                    </>,
                },
                {
                    display_row: (order) => {

                        const task = () => {

                            if (order.last_pickup_task)
                                return showPickupTaskChip(order.last_pickup_task.id);
                            return <></>;
                        }
                        return mobileDoubleItemsGrid([task(), showWorkflow(order.workflow, order.is_exchange, order.exchange_for, order.return_reason)])
                    },
                }
            ]}

            // cardFooter={(order) => { return { content: <>{order.delivery_state}</>, background: 'lightgrey' } }}
            cardIcon={(order) => <InventoryIcon color={'primary'} />}
            onCardClick={(order) => window.open(`/order/${order.id}`, "_blank")}

            cardFooter={(order) => {
                return {
                    content:
                        <IconWithText centered>
                            {getDeliveryStateIcon(order.delivery_state)}
                            <>{order.delivery_state.replaceAll("_", " ")}</>
                        </IconWithText>
                    ,
                    background: getDeliveryStateColor(order.delivery_state)
                }
            }}
        />
    </>
    
}

export default ResponsiveOrdersListing;